import { create } from 'zustand'

export type ActivitySubTab =
  | 'combo'
  | 'posts'
  | 'reposts'
  | 'replies'
  | 'likes'
  | 'overlap'
  | 'likers'

export type ActivityView = 'list' | 'gallery' | 'heatmap'

export type ActivityKind = 'post' | 'reply' | 'repost' | 'like'

export interface CoordinatedPair {
  handleA: string
  handleB: string
  uriA: string
  uriB: string
  deltaMs: number
  tsMs: number
}

/** Lightweight shape — just enough for analyzeView() to build a
 *  payload from anywhere in the app. ActivityHome owns the data and
 *  pushes it here whenever it polls. */
export interface ActivitySnapshotItem {
  id: string
  uri: string
  indexedAt: string
  fromHandle: string
  authorHandle: string
  text: string
  tags: string[]
  alts: string[]
  imageCount: number
  isAdult: boolean
  likeCount: number
  repostCount: number
  replyCount: number
  kind: ActivityKind
}

interface State {
  items: ActivitySnapshotItem[]
  subTab: ActivitySubTab
  /** Render mode for whichever sub-tab is active. */
  view: ActivityView
  /** Independent flag pair. Both on (default) = show everything;
   *  flip one to filter. Neither on intentionally returns empty. */
  sfw: boolean
  nsfw: boolean
  /** How many HOURS of history to keep visible. Drives every prune
   *  + cutoff in ActivityHome / ActivityFeed / analyzeView. 1 hour
   *  to 30 days (720h). Default 24h. */
  windowHours: number
  /** True while a backfill paginate is in flight (slider grew past
   *  what's already cached). Banner uses it to render
   *  "backfilling…". */
  backfilling: boolean
  /** Lowercase handles the operator has DESELECTED from the activity
   *  view. Default = empty = every open tab counts. New tabs are
   *  auto-included; only explicit toggle excludes. */
  excludedTabs: string[]
  /** Background-poll interval in SECONDS (clamped 30–3600). Both
   *  ActivityFeed (right panel) and ActivityHome read this; the
   *  setInterval cleanup-re-arms whenever it changes. Default 60
   *  (1 min) — fresh enough for the dashboard, just above the
   *  worker's free-tier 100k/day floor (sub-60s gets a warning chip). */
  pollIntervalSec: number
  /** Names of toolbar sections the operator has collapsed. Section
   *  keys: "stats" | "window" | "poll" | "subtabs" | "view".
   *  Empty by default. Drives the chip-mode flex-wrap packing in
   *  ActivityHome. */
  collapsedSections: string[]
  coordinatedPairs: CoordinatedPair[]
  setCoordinatedPairs: (pairs: CoordinatedPair[]) => void
  setItems: (items: ActivitySnapshotItem[]) => void
  setSubTab: (s: ActivitySubTab) => void
  setView: (v: ActivityView) => void
  setSfw: (b: boolean) => void
  setNsfw: (b: boolean) => void
  setWindowHours: (n: number) => void
  setBackfilling: (b: boolean) => void
  toggleTab: (handle: string) => void
  selectAllTabs: () => void
  excludeAllTabs: (handles: string[]) => void
  setPollIntervalSec: (n: number) => void
  toggleSection: (name: string) => void
}

const MIN_WINDOW_HOURS = 1
const MAX_WINDOW_HOURS = 24 * 30 // 30 days

export const useActivityState = create<State>((set) => ({
  items: [],
  coordinatedPairs: [],
  setCoordinatedPairs: (coordinatedPairs) => set({ coordinatedPairs }),
  subTab: 'combo',
  view: 'gallery',
  sfw: true,
  nsfw: true,
  windowHours: 24,
  backfilling: false,
  excludedTabs: [],
  pollIntervalSec: 60,
  collapsedSections: [],
  setItems: (items) => set({ items }),
  setSubTab: (subTab) => set({ subTab }),
  setView: (view) => set({ view }),
  setSfw: (sfw) => set({ sfw }),
  setNsfw: (nsfw) => set({ nsfw }),
  setWindowHours: (n) =>
    set({
      windowHours: Math.max(
        MIN_WINDOW_HOURS,
        Math.min(MAX_WINDOW_HOURS, Math.round(n)),
      ),
    }),
  setBackfilling: (b) => set({ backfilling: b }),
  toggleTab: (handle) =>
    set((s) => {
      const h = handle.toLowerCase()
      return {
        excludedTabs: s.excludedTabs.includes(h)
          ? s.excludedTabs.filter((x) => x !== h)
          : [...s.excludedTabs, h],
      }
    }),
  selectAllTabs: () => set({ excludedTabs: [] }),
  excludeAllTabs: (handles) => set({ excludedTabs: handles.map((h) => h.toLowerCase()) }),
  setPollIntervalSec: (n) =>
    set({
      pollIntervalSec: Math.max(30, Math.min(3600, Math.round(n))),
    }),
  toggleSection: (name) =>
    set((s) => ({
      collapsedSections: s.collapsedSections.includes(name)
        ? s.collapsedSections.filter((x) => x !== name)
        : [...s.collapsedSections, name],
    })),
}))

export const HOUR_MS = 60 * 60 * 1000
export const DAY_MS = 24 * HOUR_MS
